import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { cn } from '@/shared/lib/utils';

/**
 * Bouton.
 *
 * Pas d'animation d'appui, pas de lueur, pas de degrade (§0.1) : les seules transitions
 * sont des changements de couleur sous 120 ms. `danger` n'est pas une decoration — il ne
 * s'emploie que sur les actes irreversibles, pour que sa rarete lui garde son sens.
 */
const variantes = cva(
  cn(
    'bouton-anime inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-[--radius-astro]',
    'font-medium select-none',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
    'disabled:pointer-events-none disabled:opacity-45',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
  ),
  {
    variants: {
      variante: {
        principal: 'bg-accent text-accent-contraste hover:bg-accent-clair',
        secondaire: 'bg-surface-haute text-texte border border-bordure hover:bg-surface-survol',
        discret: 'text-texte-doux hover:bg-surface-haute hover:text-texte',
        danger: 'bg-danger text-white hover:brightness-110',
        lien: 'text-accent underline-offset-2 hover:underline',
      },
      taille: {
        sm: 'h-6 px-2 text-[0.75rem] [&_svg]:size-3',
        md: 'h-8 px-3 [&_svg]:size-3.5',
        icone: 'size-8 [&_svg]:size-3.5',
      },
    },
    defaultVariants: { variante: 'principal', taille: 'md' },
  },
);

export interface ProprietesBouton
  extends ComponentProps<'button'>,
    VariantProps<typeof variantes> {
  asChild?: boolean;
}

export function Bouton({ className, variante, taille, asChild, ...reste }: ProprietesBouton) {
  const Composant = asChild ? Slot : 'button';
  return <Composant className={cn(variantes({ variante, taille }), className)} {...reste} />;
}
